import { ChevronDown, ChevronUp, Mail } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  EmailStepContent,
  useGetLOAChaserFlowsQuery,
} from '@/services/api/agentflowService';

export interface EmailStepConfig {
  step_id: string;
  step_label: string;
  body: string;
}

interface LOAChaserEmailConfigProps {
  /** Flow ID to show email steps for (defaults to the default flow) */
  flowId?: string;
  /** Callback when email content changes (body only - subject comes from thread) */
  onChange: (emailStepContent: Record<string, { body: string }>) => void;
  /** Initial values to populate (optional) */
  initialValues?: Record<string, { body: string }>;
}

/**
 * Component for configuring LOA Chaser email body content.
 * Subject comes from the email thread, only body is configurable per step.
 */
export const LOAChaserEmailConfig = ({
  flowId,
  onChange,
  initialValues,
}: LOAChaserEmailConfigProps) => {
  const { data: flowsData, isLoading } = useGetLOAChaserFlowsQuery();
  const [emailConfigs, setEmailConfigs] = useState<EmailStepConfig[]>([]);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  // Initialize email configs from flow data
  useEffect(() => {
    if (!flowsData) return;

    const selectedFlowId = flowId || flowsData.default_flow_id;
    const selectedFlow = flowsData.flows.find((f) => f.flow_id === selectedFlowId);

    if (!selectedFlow) return;

    // Initialize with defaults, then override with any initial values
    const configs = selectedFlow.email_steps.map((step) => ({
      step_id: step.step_id,
      step_label: step.step_label,
      body: initialValues?.[step.step_id]?.body || step.body,
    }));

    setEmailConfigs(configs);

    // Don't expand by default - keep collapsed
    setExpandedSteps(new Set());
  }, [flowsData, flowId, initialValues]);

  // Notify parent of changes
  useEffect(() => {
    if (emailConfigs.length === 0) return;

    const content: Record<string, { body: string }> = {};
    emailConfigs.forEach((config) => {
      content[config.step_id] = {
        body: config.body,
      };
    });
    onChange(content);
  }, [emailConfigs, onChange]);

  const handleBodyChange = (stepId: string, body: string) => {
    setEmailConfigs((prev) =>
      prev.map((config) =>
        config.step_id === stepId ? { ...config, body } : config,
      ),
    );
  };

  const toggleExpanded = (stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSteps(new Set(emailConfigs.map((c) => c.step_id)));
  };

  const collapseAll = () => {
    setExpandedSteps(new Set());
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (emailConfigs.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No email steps found in this flow.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-zinc-700">
          <Mail className="h-3.5 w-3.5" />
          Follow-up Email Templates ({emailConfigs.length})
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={expandAll}
            className="text-[10px] h-6 px-2"
          >
            Expand
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={collapseAll}
            className="text-[10px] h-6 px-2"
          >
            Collapse
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        {emailConfigs.map((config, index) => (
          <Collapsible
            key={config.step_id}
            open={expandedSteps.has(config.step_id)}
            onOpenChange={() => toggleExpanded(config.step_id)}
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-between font-normal h-8 text-xs"
              >
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary">
                    {index + 1}
                  </span>
                  <span className="truncate">{config.step_label}</span>
                </span>
                {expandedSteps.has(config.step_id) ? (
                  <ChevronUp className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="space-y-1">
                <Label htmlFor={`${config.step_id}-body`} className="text-xs text-zinc-600">
                  Email Body
                </Label>
                <Textarea
                  id={`${config.step_id}-body`}
                  value={config.body}
                  onChange={(e) =>
                    handleBodyChange(config.step_id, e.target.value)
                  }
                  placeholder="Email body..."
                  rows={5}
                  className="resize-y text-sm"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </div>
  );
}

/**
 * Hook to get email step content for a flow.
 * Returns the default content that can be used as initial values.
 */
export function useDefaultEmailContent(flowId?: string) {
  const { data: flowsData, isLoading } = useGetLOAChaserFlowsQuery();

  if (isLoading || !flowsData) {
    return { emailSteps: [] as EmailStepContent[], isLoading };
  }

  const selectedFlowId = flowId || flowsData.default_flow_id;
  const selectedFlow = flowsData.flows.find((f) => f.flow_id === selectedFlowId);

  return {
    emailSteps: selectedFlow?.email_steps || [],
    isLoading,
  };
}
